(() => {
  const fineHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const hoverTimers = new WeakMap();
  const videoStartTime = 1.2;

  const seekToStart = (video) => {
    if (video.readyState === 0) {
      return;
    }

    const latestSafeStart = Number.isFinite(video.duration)
      ? Math.max(0, video.duration - 0.05)
      : videoStartTime;

    video.currentTime = Math.min(videoStartTime, latestSafeStart);
  };

  const defaultCards = new Map();
  const defaultSelector = 'product-card[data-default-video]';

  const wantsDefaultVideo = (card, state) => {
    const media = card.querySelector('.product-card__media');
    return card.isConnected && state.visible && !state.paused && !state.failed &&
      !reducedMotion.matches && !document.hidden &&
      !(fineHover.matches && card.matches(':hover')) &&
      media.scrollLeft < media.clientWidth / 2;
  };

  const syncDefaultVideo = async (card) => {
    const state = defaultCards.get(card);
    if (!state) return;
    const video = card.querySelector('.product-card__hover-video');
    const button = card.querySelector('[data-card-video-toggle]');
    button.setAttribute('aria-pressed', String(!state.paused && !state.failed));

    if (!wantsDefaultVideo(card, state)) {
      state.attempt++;
      state.pending = false;
      card.classList.remove('nfd-default-video-playing');
      video.pause();
      return;
    }

    if (state.pending || !video.paused) return;
    state.pending = true;
    const attempt = ++state.attempt;
    video.muted = true;
    const source = video.querySelector('source[data-src]');
    if (source) {
      video.addEventListener('loadedmetadata', () => seekToStart(video), { once: true });
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
      video.load();
    }

    try {
      await video.play();
      if (attempt === state.attempt && wantsDefaultVideo(card, state)) {
        card.classList.add('nfd-default-video-playing');
      } else if (!wantsDefaultVideo(card, state)) {
        video.pause();
      }
    } catch (error) {
      if (attempt === state.attempt) {
        state.failed = true;
        card.classList.remove('nfd-default-video-playing');
        video.pause();
        button.setAttribute('aria-pressed', 'false');
      }
    } finally {
      if (attempt === state.attempt) state.pending = false;
    }
  };

  const visibilityObserver = new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      const state = defaultCards.get(target);
      if (state) {
        state.visible = isIntersecting;
        syncDefaultVideo(target);
      }
    });
  });

  const registerDefaultCards = (root) => {
    const cards = [...root.querySelectorAll(defaultSelector)];
    if (root.matches?.(defaultSelector)) cards.push(root);
    cards.forEach((card) => {
      if (defaultCards.has(card)) return;
      const state = { visible: false, paused: false, failed: false, pending: false, attempt: 0 };
      defaultCards.set(card, state);
      card.querySelector('[data-card-video-toggle]').addEventListener('click', () => {
        state.paused = !state.paused && !state.failed;
        state.failed = false;
        syncDefaultVideo(card);
      });
      card.querySelector('.product-card__media').addEventListener('scroll', () => syncDefaultVideo(card), { passive: true });
      card.querySelector('video').addEventListener('error', () => {
        state.failed = true;
        syncDefaultVideo(card);
      });
      visibilityObserver.observe(card);
    });
  };

  const updateCapabilities = () => {
    document.documentElement.classList.toggle('nfd-card-default-video-enabled', !reducedMotion.matches);
    document.documentElement.classList.toggle('nfd-card-hover-video-enabled', fineHover.matches && !reducedMotion.matches);
    defaultCards.forEach((state, card) => syncDefaultVideo(card));
    if (reducedMotion.matches || !fineHover.matches) {
      document.querySelectorAll('product-card[data-hover-video]:not([data-default-video])').forEach(stopVideo);
    }
  };

  registerDefaultCards(document);
  const collectionSection = document.querySelector('#collection')?.closest('.shopify-section');
  if (collectionSection) new MutationObserver((mutations) => {
    defaultCards.forEach((state, card) => {
      if (!card.isConnected) {
        syncDefaultVideo(card);
        visibilityObserver.unobserve(card);
        defaultCards.delete(card);
      }
    });
    mutations.forEach(({ addedNodes }) => addedNodes.forEach((node) => {
      if (node.nodeType === 1) registerDefaultCards(node);
    }));
  }).observe(collectionSection, { childList: true, subtree: true });
  reducedMotion.addEventListener('change', updateCapabilities);
  fineHover.addEventListener('change', updateCapabilities);
  document.addEventListener('visibilitychange', () => defaultCards.forEach((state, card) => syncDefaultVideo(card)));

  const stopVideo = (card) => {
    const timer = hoverTimers.get(card);

    if (timer) {
      window.clearTimeout(timer);
      hoverTimers.delete(card);
    }

    const video = card.querySelector('.product-card__hover-video');
    card.classList.remove('nfd-hover-video-playing');

    if (!video) {
      return;
    }

    video.pause();
    seekToStart(video);
  };

  const loadAndPlay = async (card) => {
    const video = card.querySelector('.product-card__hover-video');

    if (!video || !fineHover.matches || reducedMotion.matches || !card.matches(':hover')) {
      return;
    }

    const unloadedSources = video.querySelectorAll('source[data-src]');
    const metadataReady = video.readyState > 0
      ? Promise.resolve()
      : new Promise((resolve, reject) => {
          video.addEventListener('loadedmetadata', resolve, { once: true });
          video.addEventListener('error', reject, { once: true });
        });

    if (unloadedSources.length > 0) {
      unloadedSources.forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });

      video.load();
    }

    video.muted = true;

    try {
      await metadataReady;

      if (!fineHover.matches || reducedMotion.matches || !card.matches(':hover')) {
        stopVideo(card);
        return;
      }

      seekToStart(video);
      await video.play();

      if (fineHover.matches && !reducedMotion.matches && card.matches(':hover')) {
        card.classList.add('nfd-hover-video-playing');
      } else {
        stopVideo(card);
      }
    } catch (error) {
      card.classList.remove('nfd-hover-video-playing');
    }
  };

  document.addEventListener('pointerover', (event) => {
    const card = event.target.closest('product-card[data-hover-video]');

    if (!card || card.contains(event.relatedTarget)) {
      return;
    }

    if (card.hasAttribute('data-default-video')) {
      syncDefaultVideo(card);
      return;
    }
    if (!fineHover.matches || reducedMotion.matches) return;

    hoverTimers.set(card, window.setTimeout(() => {
      hoverTimers.delete(card);
      loadAndPlay(card);
    }, 50));
  });

  document.addEventListener('pointerout', (event) => {
    const card = event.target.closest('product-card[data-hover-video]');

    if (!card || card.contains(event.relatedTarget)) {
      return;
    }

    if (card.hasAttribute('data-default-video')) {
      syncDefaultVideo(card);
    } else {
      stopVideo(card);
    }
  });

  updateCapabilities();
})();
